import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { faqJsonLd, serviceBreadcrumbJsonLd, serviceJsonLd } from "@/lib/jsonld-servicios";
import { AnfitrionasPageContent } from "@/components/site/AnfitrionasPageContent";
import { orderForHeroCollage } from "@/lib/roster-hero-photo";
import heroImg from "@/assets/anfitrionas.jpg";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { applyServiceOverride, type ServiceSchemaData } from "@/lib/seo-schema-apply";
import { getPromotorasGalleryPublic } from "@/server/site/promotoras-gallery.fns";
import { seo } from "@/content/pages/anfitrionas.seo";

const DEFAULT_TITLE = seo.title;
const DEFAULT_DESCRIPTION = seo.description;

const faqs = [
  {
    q: "¿Qué es una anfitriona de eventos?",
    a: "Es el personal que representa al organizador frente a los invitados: recepción, registro, orientación y protocolo. Su foco es la experiencia del invitado, no la venta de un producto.",
  },
  {
    q: "¿Cuánto cuesta contratar anfitrionas en Lima?",
    a: "Depende del número de anfitrionas, las horas y jornadas, el perfil (idiomas, protocolo), el uniforme y la supervisión. Te enviamos un presupuesto desglosado según tu evento.",
  },
  {
    q: "¿Cuál es la diferencia entre contratar anfitrionas freelance y hacerlo a través de una agencia?",
    a: "Al contratar directamente, tu empresa asume la selección, el briefing, la coordinación y la gestión de cualquier ausencia. Con Improve Yourself, esas tareas forman parte del servicio acordado, con supervisión en sitio y coordinación de reemplazos durante el evento.",
  },
  {
    q: "¿Qué pasa si una anfitriona falta el día del evento?",
    a: "Nuestro servicio contempla reemplazo ante ausencias. El supervisor responsable coordina la respuesta y mantiene la comunicación con el organizador para cubrir la función afectada.",
  },
  {
    q: "¿Con cuánta anticipación se debe reservar el servicio?",
    a: "Solicita la propuesta en cuanto tengas confirmados la fecha, el lugar y el horario. Una mayor anticipación facilita la selección de perfiles, especialmente si necesitas inglés, varias jornadas o atención fuera de Lima. Para solicitudes cercanas a la fecha, consulta disponibilidad antes de cerrar la planificación.",
  },
  {
    q: "¿Cuántas anfitrionas necesita mi evento?",
    a: "Depende del flujo de llegada, los puestos de registro, los accesos y los ambientes simultáneos. Dimensionamos el equipo contigo a partir de la agenda y las funciones de cada puesto.",
  },
];

const props = {
  eyebrow: "Anfitrionas para eventos",
  h1: "Anfitrionas para eventos en Lima: recepción, registro y protocolo",
  intro:
    "La experiencia de tus invitados empieza en la recepción. Seleccionamos y preparamos a las anfitrionas de eventos que necesitas, con supervisión en sitio durante cada jornada.",
};

export const Route = createFileRoute("/anfitrionas")({
  loader: () => getPromotorasGalleryPublic({ data: { pagina: "anfitrionas" } }),
  component: AnfitrionasPage,
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const heroPhoto = orderForHeroCollage((loaderData ?? []).slice(0, 5))[0]?.url;
    // "servicio-anfitrionas" (specs/111-slots-imagenes-sitio) solo alimenta el
    // meta OG/JSON-LD acá — esta página no renderiza un hero estático visible
    // (usa una foto destacada del roster), a diferencia de las páginas
    // basadas en ServicePage.
    const slot = match.context.siteImageSlots?.["servicio-anfitrionas"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/anfitrionas",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/anfitrionas"),
        ...(heroPhoto
          ? [
              // Preconnect al origen de Supabase Storage: solo acá y en
              // /impulsadoras-promotoras, las únicas rutas que sirven fotos
              // reales de la galería directo desde ese storage (PageSpeed
              // marcaba este preconnect como "no utilizado" cuando vivía
              // global en __root.tsx, porque el resto del sitio no lo pide).
              { rel: "preconnect", href: new URL(heroPhoto).origin },
              { rel: "preload", as: "image", href: heroPhoto, fetchPriority: "high" as const },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            applyServiceOverride(
              serviceJsonLd(props.h1, props.intro, "/anfitrionas", slot?.src ?? heroImg),
              override?.schemaType === "service"
                ? (override.schemaData as Partial<ServiceSchemaData>)
                : null,
            ),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(serviceBreadcrumbJsonLd(props.eyebrow, "/anfitrionas")),
        },
      ],
    };
  },
});

function AnfitrionasPage() {
  const gallery = Route.useLoaderData();
  return (
    <PageShell>
      <AnfitrionasPageContent {...props} gallery={gallery} faqs={faqs} />
    </PageShell>
  );
}
